import { useEffect, useState } from "react";

/**
 * Sound is on by default and the choice is remembered on the device.
 * Browsers only allow sound after the visitor has interacted with the page,
 * so on a first visit the first tap, scroll or key press unlocks it.
 */
const STORAGE_KEY = "byteline-social-sound";

function readStoredSoundOn(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
}

function storeSoundOn(value: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? "on" : "off");
  } catch {
    // Storage unavailable; sound still works for this visit.
  }
}

let soundOn = readStoredSoundOn();
let locked = false;
const listeners = new Set<(value: boolean) => void>();

function setSoundOn(value: boolean) {
  // A manual toggle is a visitor choice: stop waiting for an unlock gesture.
  locked = false;
  soundOn = value;
  storeSoundOn(value);
  listeners.forEach((fn) => fn(value));
}

/**
 * The browser refused to play with sound. Mute for now and turn sound back
 * on automatically at the visitor's first tap, scroll or key press.
 */
function markSoundLocked() {
  if (locked) return;
  locked = true;
  soundOn = false;
  listeners.forEach((fn) => fn(false));
  startListening();
}

let listening = false;

function startListening() {
  if (listening || typeof window === "undefined") return;
  listening = true;
  const unlock = () => {
    if (locked) {
      locked = false;
      soundOn = true;
      storeSoundOn(true);
      listeners.forEach((fn) => fn(true));
    }
    listening = false;
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("wheel", unlock);
    window.removeEventListener("touchstart", unlock);
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
  window.addEventListener("wheel", unlock, { once: true });
  window.addEventListener("touchstart", unlock, { once: true });
}

export function useSound() {
  const [value, setValue] = useState(soundOn);

  useEffect(() => {
    listeners.add(setValue);
    startListening();
    setValue(soundOn);
    return () => {
      listeners.delete(setValue);
    };
  }, []);

  return { soundOn: value, setSoundOn, markSoundLocked };
}
